import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { useSEO } from '@/hooks/useSEO';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/hooks/useAuth';
import { useFavorites } from '@/hooks/useFavorites';
import { usePodcasts } from '@/hooks/usePodcasts';
import { useCityTours } from '@/hooks/useCityTours';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useNavigate, useLocation } from 'react-router-dom';

// Lazy load non-critical components for better performance
const Footer = lazy(() => import('@/components/Footer'));
const FAQ = lazy(() => import('@/components/FAQ'));
const AirbnbHeaderLazy = lazy(() => import('@/components/AirbnbHeader'));
const PodcastGenerator = lazy(() => import('@/components/PodcastGenerator'));
const MobilePodcastGenerator = lazy(() => import('@/components/MobilePodcastGenerator'));

// Critical components - load immediately
import AirbnbSearchBar from '@/components/AirbnbSearchBar';
import NavigationTabs, { TabType } from '@/components/NavigationTabs';
import VisitCarousel from '@/components/VisitCarousel';
import TourCarousel from '@/components/TourCarousel';
import CarouselSkeleton from '@/components/CarouselSkeleton';

// Services
import { getUserLocation, getCityFromLocation, calculateDistance, findNearestCityWithPlaces } from '@/services/locationService';
import type { LocationData } from '@/services/locationService';

import { Podcast } from '@/types/Podcast';
import { useNavigationState } from '@/hooks/useNavigationState';

const Index = () => {
  // State management
  const [activeTab, setActiveTab] = useState<TabType>('visits');
  const [searchQuery, setSearchQuery] = useState('');
  const [userLocation, setUserLocation] = useState<LocationData | null>(null);
  const [cityName, setCityName] = useState('Paris'); // Default to Paris as specified
  const [showPodcastGenerator, setShowPodcastGenerator] = useState(false);
  const [initialSearchText, setInitialSearchText] = useState('');

  // Hooks
  const { podcasts, loading: podcastsLoading, searchPodcasts } = usePodcasts();
  const { tours, loading: toursLoading } = useCityTours();
  const { toast } = useToast();
  const { language, toggleLanguage, t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isScrolled = useScrollPosition(80);
  const { navigateToPlace } = useNavigationState();

  // SEO
  useSEO({
    title: 'DiskOver - Découvrez le monde à travers des podcasts culturels',
    description: 'Explorez des lieux culturels du monde entier avec nos podcasts audio immersifs. Visites guidées et tours culturels gratuits.',
    canonical: 'https://diskover.ovh/',
    keywords: 'podcasts culturels, visites guidées audio, exploration culturelle, voyages virtuels'
  });

  // Get user location on mount and find best city
  useEffect(() => {
    const initLocation = async () => {
      try {
        const location = await getUserLocation();
        setUserLocation(location);
        
        // Wait for podcasts to be loaded to find the best city
        if (podcasts.length > 0) {
          const bestCity = findNearestCityWithPlaces(podcasts, location, 100);
          setCityName(bestCity);
        } else {
          setCityName('Paris'); // Default fallback
        }
      } catch (error) {
        console.error('Failed to get user location:', error);
        setCityName('Paris'); // Keep default Paris
      }
    };

    initLocation();
  }, [podcasts]); // Add podcasts dependency

  // Handle search results
  const handleSearchResults = (results: Podcast[]) => {
    // Filter displayed content based on search results
    console.log('Search results:', results);
    // The search is now handled by the AirbnbSearchBar component itself
    // with navigation to place details
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Handle podcast selection
  const handlePodcastSelect = (podcast: Podcast) => {
    navigateToPlace(podcast.Name);
  };

  // Handle tour selection
  const handleTourSelect = (tour: any) => {
    // Navigate to tours page with selected tour and zoom on map
    navigate('/tours', { state: { selectedTour: tour, zoomOnTour: true } });
  };

  // Handle title clicks
  const handleTourTitleClick = () => {
    navigate('/tours');
  };

  const handleVisitTitleClick = () => {
    navigate('/map');
  };

  // Handle create visit from search
  const handleCreateVisit = (searchText: string) => {
    setInitialSearchText(searchText);
    setShowPodcastGenerator(true);
  };

  const handleClosePodcastGenerator = () => {
    setShowPodcastGenerator(false);
    setInitialSearchText('');
  };

  const handlePodcastGenerated = (podcast: any) => {
    toast({
      title: 'Visite créée',
      description: `Le podcast pour ${podcast.Name} est maintenant disponible.`,
    });
    setShowPodcastGenerator(false);
    setInitialSearchText('');
    // Navigate to the new place
    navigateToPlace(podcast.Name);
  };

  // Filter functions
  const getFilteredPodcasts = () => {
    let filtered = podcasts;
    
    if (searchQuery) {
      filtered = searchPodcasts(searchQuery);
    }
    
    return filtered;
  };

  // Memoized filtering functions for better performance
  const getNearbyPodcasts = useMemo(() => {
    if (!userLocation) {
      // Fallback to Paris area or city with most places
      return podcasts
        .filter(podcast => podcast.City && (podcast.City.toLowerCase() === 'paris' || podcast.City.toLowerCase() === cityName.toLowerCase()))
        .slice(0, 10);
    }
    
    const podcastsWithDistance = podcasts.map(podcast => ({
      ...podcast,
      distance: calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        podcast.Latitude,
        podcast.Longitude
      )
    }));

    return podcastsWithDistance
      .filter(p => p.distance <= 100) // Within 100km
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 10);
  }, [podcasts, userLocation, cityName]);

  const getHighRatedPodcasts = useMemo(() => {
    let highRatedPodcasts = podcasts.filter(podcast => podcast.Note_Google && podcast.Note_Google > 4.5);
    
    if (!userLocation) {
      return highRatedPodcasts.slice(0, 10);
    }
    
    // Prioritize nearby highly-rated places
    const podcastsWithDistance = highRatedPodcasts.map(podcast => ({
      ...podcast,
      distance: calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        podcast.Latitude,
        podcast.Longitude
      )
    }));
    
    return podcastsWithDistance
      .sort((a, b) => {
        // First by distance (closer first), then by rating (higher first)
        if (a.distance <= 100 && b.distance > 100) return -1;
        if (b.distance <= 100 && a.distance > 100) return 1;
        if (a.distance !== b.distance) return a.distance - b.distance;
        return (b.Note_Google || 0) - (a.Note_Google || 0);
      })
      .slice(0, 10);
  }, [podcasts, userLocation]);

  const getParisMuseums = () => {
    // Paris coordinates (48.8566, 2.3522)
    const parisLat = 48.8566;
    const parisLng = 2.3522;
    
    let museumPodcasts = podcasts.filter(podcast => 
      podcast.Type_Google && 
      (podcast.Type_Google.toLowerCase().includes('museum') || 
       podcast.Type_Google.toLowerCase().includes('art museum'))
    );
    
    // Filter museums within 50km of Paris
    const parisMuseums = museumPodcasts.filter(podcast => {
      const distance = calculateDistance(
        parisLat,
        parisLng,
        podcast.Latitude,
        podcast.Longitude
      );
      return distance <= 50;
    });
    
    return parisMuseums
      .sort((a, b) => (b.Note_Google || 0) - (a.Note_Google || 0))
      .slice(0, 10);
  };

  const getBuddhistTemples = () => {
    const buddhistTemples = podcasts.filter(podcast => {
      const descriptionFR = podcast.Description_FR?.toLowerCase() || '';
      const descriptionEN = podcast.Description_EN?.toLowerCase() || '';
      const name = podcast.Name?.toLowerCase() || '';
      const typeGoogle = podcast.Type_Google?.toLowerCase() || '';
      
      return descriptionFR.includes('temple bouddhiste') || 
             descriptionEN.includes('buddhist temple') ||
             typeGoogle.includes('buddhist temple') ||
             name.includes('temple') && (descriptionFR.includes('bouddhiste') || descriptionEN.includes('buddhist'));
    });
    
    return buddhistTemples.slice(0, 10);
  };

  const getTokyoPlaces = () => {
    const tokyoPlaces = podcasts.filter(podcast => 
      podcast.City && podcast.City.toLowerCase().includes('tokyo')
    );
    
    return tokyoPlaces
      .sort((a, b) => (b.Note_Google || 0) - (a.Note_Google || 0))
      .slice(0, 10);
  };

  // Favorites filtering functions
  const getFavoritePodcasts = () => {
    if (!isAuthenticated || favorites.length === 0) return [];
    
    const favoritePodcasts = podcasts.filter(podcast => 
      favorites.includes(podcast.Name)
    );
    
    return favoritePodcasts
      .sort((a, b) => (b.Note_Google || 0) - (a.Note_Google || 0))
      .slice(0, 10);
  };

  const getFavoriteTours = () => {
    if (!isAuthenticated || favorites.length === 0) return [];
    
    const favoriteTours = tours.filter(tour => 
      favorites.includes(tour.name)
    );
    
    return favoriteTours.slice(0, 10);
  };

  // Helper function to deduplicate tours by city using coordinates
  const deduplicateToursByCity = (toursList: any[]) => {
    const uniqueTours: any[] = [];
    const seenLocations = new Set<string>();
    
    toursList.forEach(tour => {
      if (tour.latitude && tour.longitude) {
        // Create a location key with rounded coordinates (to handle small differences)
        const locationKey = `${Math.round(tour.latitude * 1000)}_${Math.round(tour.longitude * 1000)}`;
        
        if (!seenLocations.has(locationKey)) {
          seenLocations.add(locationKey);
          uniqueTours.push(tour);
        }
      } else {
        // If no coordinates, use city name as fallback
        const cityKey = tour.city?.toLowerCase();
        if (cityKey && !seenLocations.has(cityKey)) {
          seenLocations.add(cityKey);
          uniqueTours.push(tour);
        }
      }
    });
    
    return uniqueTours;
  };

  const getNearbyTours = () => {
    if (!userLocation) {
      // Fallback to tours in the selected city/country
      const localTours = tours.filter(tour => 
        tour.city.toLowerCase().includes(cityName.toLowerCase()) ||
        tour.country.toLowerCase() === 'france'
      );
      return deduplicateToursByCity(localTours);
    }
    
    const toursWithDistance = tours.map(tour => ({
      ...tour,
      distance: tour.latitude && tour.longitude ? calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        tour.latitude,
        tour.longitude
      ) : Infinity
    }));

    const nearbyTours = toursWithDistance
      .filter(t => t.distance <= 500) // Within 500km for tours
      .sort((a, b) => a.distance - b.distance);
      
    return deduplicateToursByCity(nearbyTours);
  };

  const getEssentialTours = () => {
    const essentialTours = tours.filter(tour => 
      tour.style && tour.style.toLowerCase().includes('must do')
    );
    return deduplicateToursByCity(essentialTours);
  };

  const getFrenchTours = () => {
    const frenchTours = tours.filter(tour => 
      tour.country.toLowerCase() === 'france'
    );
    return deduplicateToursByCity(frenchTours);
  };

  const getLocalTours = () => {
    const localTours = tours.filter(tour => 
      tour.style && tour.style.toLowerCase().includes('hors des sentiers')
    );
    return deduplicateToursByCity(localTours);
  };

  const getItalianTours = () => {
    const italianTours = tours.filter(tour => 
      tour.country.toLowerCase() === 'italy'
    );
    return deduplicateToursByCity(italianTours);
  };

  // Progressive loading - show UI immediately with skeletons for loading content
  const isInitialLoading = podcastsLoading && podcasts.length === 0;

  return (
    <div className={`bg-white ${isMobile ? 'min-h-screen flex flex-col' : 'min-h-screen'}`}>

      {/* Desktop Header - Lazy load for performance */}
      {!isMobile && (
        <Suspense fallback={<div className="h-20 bg-white" />}>
          <AirbnbHeaderLazy 
            onLanguageChange={toggleLanguage} 
            podcasts={podcasts}
            onSearchResults={handleSearchResults}
            onPodcastSelect={handlePodcastSelect}
            onCreateVisit={handleCreateVisit}
          />
        </Suspense>
      )}

      {/* Sticky Header */}
      {isMobile ? (
        <div className={`${isMobile ? 'flex-shrink-0' : 'sticky top-0'} z-40 bg-white transition-all duration-300 ${
          isScrolled ? 'shadow-md' : ''
        }`}>
          {/* Search Bar - Mobile only */}
          <AirbnbSearchBar 
            podcasts={podcasts}
            onSearchResults={handleSearchResults}
            onPodcastSelect={handlePodcastSelect}
            onCreateVisit={handleCreateVisit}
            placeholder="diskover a place"
            language={language}
            isCompact={isScrolled}
          />

          {/* Navigation Tabs */}
          <NavigationTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            isCompact={isScrolled}
          />
        </div>
      ) : (
        <div className={`sticky top-0 z-40 bg-white transition-all duration-300 ${
          isScrolled ? 'shadow-md' : ''
        }`}>
          {/* Navigation Tabs - Desktop only */}
          <NavigationTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            isCompact={isScrolled}
          />
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {/* Content Area */}
        <div className={isMobile ? 'pb-24' : 'pb-20'}>
          {activeTab === 'visits' && (
            <div className="space-y-8 mt-6">
              {/* Show skeleton while initial loading, then show content */}
              {isInitialLoading ? (
                <>
                  <CarouselSkeleton title={`Places of Interest in ${cityName}`} />
                  <CarouselSkeleton title="Top-Rated Places" />
                  <CarouselSkeleton title="Museums in Paris" />
                </>
              ) : (
                <>
                  {/* Nearby Places - First carousel with priority LCP image */}
                  <VisitCarousel
                    title={`Places of Interest in ${cityName}`}
                    podcasts={getNearbyPodcasts}
                    userLocation={userLocation ? [userLocation.latitude, userLocation.longitude] : null}
                    onPodcastSelect={handlePodcastSelect}
                    showDistance={true}
                    onTitleClick={handleVisitTitleClick}
                    isFirstCarousel={true}
                  />

                  {/* High Rated Places */}
                  <VisitCarousel
                    title="Top-Rated Places"
                    podcasts={getHighRatedPodcasts}
                    userLocation={userLocation ? [userLocation.latitude, userLocation.longitude] : null}
                    onPodcastSelect={handlePodcastSelect}
                    onTitleClick={handleVisitTitleClick}
                  />

                  {/* Favorite Places - Only show if user is authenticated and has favorites */}
                  {isAuthenticated && getFavoritePodcasts().length > 0 && (
                    <VisitCarousel
                      title="My Favorite Visits"
                      podcasts={getFavoritePodcasts()}
                      userLocation={userLocation ? [userLocation.latitude, userLocation.longitude] : null}
                      onPodcastSelect={handlePodcastSelect}
                      onTitleClick={handleVisitTitleClick}
                    />
                  )}

                  {/* Museums in Paris */}
                  <VisitCarousel
                    title="Museums in Paris"
                    podcasts={getParisMuseums()}
                    userLocation={userLocation ? [userLocation.latitude, userLocation.longitude] : null}
                    onPodcastSelect={handlePodcastSelect}
                    onTitleClick={handleVisitTitleClick}
                  />

                  {/* Buddhist Temples */}
                  <VisitCarousel
                    title="Buddhist Temples Around the World"
                    podcasts={getBuddhistTemples()}
                    userLocation={userLocation ? [userLocation.latitude, userLocation.longitude] : null}
                    onPodcastSelect={handlePodcastSelect}
                    onTitleClick={handleVisitTitleClick}
                  />

                  {/* Tokyo Places */}
                  <VisitCarousel
                    title="What to Do in Tokyo"
                    podcasts={getTokyoPlaces()}
                    userLocation={userLocation ? [userLocation.latitude, userLocation.longitude] : null}
                    onPodcastSelect={handlePodcastSelect}
                    onTitleClick={handleVisitTitleClick}
                  />
                </>
              )}
            </div>
          )}

          {activeTab === 'tours' && (
            <div className="space-y-8 mt-6">
              {/* Show skeleton while initial loading */}
              {toursLoading && tours.length === 0 ? (
                <>
                  <CarouselSkeleton title="Nearby Tours" />
                  <CarouselSkeleton title="Must-See Essentials" />
                  <CarouselSkeleton title="Tours in France" />
                </>
              ) : (
                <>
                  {/* Nearby Tours - First carousel with priority LCP image */}
                  {getNearbyTours().length > 0 && (
                    <TourCarousel
                      title="Nearby Tours"
                      tours={getNearbyTours()}
                      onTourSelect={handleTourSelect}
                      onTitleClick={handleTourTitleClick}
                      isFirstCarousel={true}
                    />
                  )}

                  {/* Essential Tours */}
                  {getEssentialTours().length > 0 && (
                    <TourCarousel
                      title="Must-See Essentials"
                      tours={getEssentialTours()}
                      onTourSelect={handleTourSelect}
                      onTitleClick={handleTourTitleClick}
                    />
                  )}

                  {/* Favorite Tours - Only show if user is authenticated and has favorites */}
                  {isAuthenticated && getFavoriteTours().length > 0 && (
                    <TourCarousel
                      title="My Favorite Tours"
                      tours={getFavoriteTours()}
                      onTourSelect={handleTourSelect}
                      onTitleClick={handleTourTitleClick}
                    />
                  )}

                  {/* French Tours */}
                  {getFrenchTours().length > 0 && (
                    <TourCarousel
                      title="Tours in France"
                      tours={getFrenchTours()}
                      onTourSelect={handleTourSelect}
                      onTitleClick={handleTourTitleClick}
                    />
                  )}

                  {/* Local Tours */}
                  {getLocalTours().length > 0 && (
                    <TourCarousel
                      title="Travel Like a Local"
                      tours={getLocalTours()}
                      onTourSelect={handleTourSelect}
                      onTitleClick={handleTourTitleClick}
                    />
                  )}

                  {/* Italian Tours */}
                  {getItalianTours().length > 0 && (
                    <TourCarousel
                      title="Tours in Italy"
                      tours={getItalianTours()}
                      onTourSelect={handleTourSelect}
                      onTitleClick={handleTourTitleClick}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className={`bg-white border-t border-gray-200 mt-12 ${isMobile ? 'mb-20' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Footer Links */}
          <Suspense fallback={<div className="h-16 bg-gray-100 animate-pulse rounded" />}>
            <Footer />
          </Suspense>
        </div>
      </footer>

      {/* Podcast Generator Modal */}
      <Suspense fallback={null}>
        {isMobile ? (
          <MobilePodcastGenerator 
            isOpen={showPodcastGenerator} 
            onClose={handleClosePodcastGenerator} 
            onPodcastGenerated={handlePodcastGenerated} 
            initialSearchText={initialSearchText} 
          />
        ) : (
          <PodcastGenerator 
            isOpen={showPodcastGenerator} 
            onClose={handleClosePodcastGenerator} 
            onPodcastGenerated={handlePodcastGenerated} 
            initialSearchText={initialSearchText} 
          />
        )}
      </Suspense>
    </div>
  );
};

export default Index;
